import { Injectable } from '@angular/core';
import { Profile } from '@icc/window-door/types';
import { ConfigurationsService } from '@icc/configurator/shared';
import { core } from '@icc/shared/helpers';

@Injectable()
export class ProfilesUpdateService {
    constructor(
        protected configurationsService: ConfigurationsService<'door'>,
    ) {}

    static nextNegativeId(
        usedProfiles: Array<{ id?: number | string }> = [],
    ): number {
        const numericIds = usedProfiles
            .map(p => Number(p?.id))
            .filter(n => Number.isFinite(n));
        const minId = numericIds.length ? Math.min(...numericIds, 0) : 0;
        return minId <= 0 ? minId - 1 : -1;
    }

    static cloneVariableWidthProfile(
        profile: Profile,
        usedProfiles: Array<{ id?: number | string }> = [],
    ): Profile {
        const cloned = core.copy(profile) as Profile & { _clonedFrom?: number; id?: number };
        cloned._clonedFrom = profile.id;
        cloned.id = ProfilesUpdateService.nextNegativeId(usedProfiles);
        usedProfiles.push(cloned);
        return cloned as Profile;
    }

    updateProfileValues(profile: Profile, delta: number, coupling?: any, clampedWidth?: number, pageData?: any) {
        if (!profile || !profile.widthOutType) {
            return;
        }
        const profileId = Number(profile?.id ?? (pageData?.selectedProfile?.id));
        const deltaValue = Number.isFinite(delta) ? delta : 0;
        const halfDelta = deltaValue / 2;
        if(profile.type == 'frame')
        {
            if (profile.spaceGlass !== null && profile.spaceGlass !== undefined) {
                profile.spaceGlass = Number(profile.spaceGlass) + deltaValue;
            }
            if (profile.spaceSash !== null && profile.spaceSash !== undefined) {
                profile.spaceSash = Number(profile.spaceSash) + deltaValue;
            }
            if (profile.spaceMullion !== null && profile.spaceMullion !== undefined) {
                profile.spaceMullion = Number(profile.spaceMullion) + deltaValue;
            }
            if (profile.spaceAlignment !== null && profile.spaceAlignment !== undefined) {
                profile.spaceAlignment = Number(profile.spaceAlignment) + deltaValue;
            }
        }
        else {
           if (profile.spaceGlass !== null && profile.spaceGlass !== undefined) {
                profile.spaceGlass = Number(profile.spaceGlass) + halfDelta;
            }
            if (profile.spaceSash !== null && profile.spaceSash !== undefined) {
                profile.spaceSash = Number(profile.spaceSash) + halfDelta;
            }
            if (profile.spaceMullion !== null && profile.spaceMullion !== undefined) {
                profile.spaceMullion = Number(profile.spaceMullion) + halfDelta;
            }
            if (profile.spaceAlignment !== null && profile.spaceAlignment !== undefined) {
                profile.spaceAlignment = Number(profile.spaceAlignment) + halfDelta;
            } 
        }
        

        const conf = this.configurationsService.conf.Current;
        const selectedWidth = clampedWidth ?? profile?.selectedWidth ?? profile.widthOut;

        const resolveFrameId = (frameRef: any): number | null => {
            const rawId = frameRef && typeof frameRef === 'object' ? frameRef.id : frameRef;
            const numericId = Number(rawId);
            return Number.isFinite(numericId) ? numericId : null;
        };
        const getFrameGapWidth = (targetCoupling: any): number | null => {
            if (!targetCoupling || !Array.isArray(conf?.Frames)) {
                return null;
            }
            const framesId = Array.isArray(targetCoupling.framesId) ? targetCoupling.framesId : [];
            const otherFramesId = Array.isArray(targetCoupling.otherFramesId)
                ? targetCoupling.otherFramesId
                : [];
            if (!framesId.length || !otherFramesId.length) {
                return null;
            }

            const firstFrames = framesId
                .map(resolveFrameId)
                .filter((id): id is number => id !== null)
                .map((id) => conf.Frames.find((f: any) => Number(f.id) === id))
                .filter((f: any) => !!f);
            const secondFrames = otherFramesId
                .map(resolveFrameId)
                .filter((id): id is number => id !== null)
                .map((id) => conf.Frames.find((f: any) => Number(f.id) === id))
                .filter((f: any) => !!f);

            if (!firstFrames.length || !secondFrames.length) {
                return null;
            }

            if (targetCoupling.direction === 'vertical') {
                const left = Math.max(...firstFrames.map((f: any) => Number(f.x) + Number(f.width)));
                const right = Math.min(...secondFrames.map((f: any) => Number(f.x)));
                const gap = right - left;
                return Number.isFinite(gap) && gap > 0 ? gap : null;
            }

            const top = Math.max(...firstFrames.map((f: any) => Number(f.y) + Number(f.height)));
            const bottom = Math.min(...secondFrames.map((f: any) => Number(f.y)));
            const gap = bottom - top;
            return Number.isFinite(gap) && gap > 0 ? gap : null;
        };
        const couplingInnerWidthFromGeometry = profile.type === 'coupling'
            ? getFrameGapWidth(coupling)
            : null;

        if (pageData?.selectedProfile?.width && pageData.selectedProfile.width !== undefined && pageData.selectedProfile.width !== null) {
            profile.width = pageData.selectedProfile.width + deltaValue;
        }
        else{
            profile.width = profile.width + deltaValue;
        }
        const newInnerWidth = couplingInnerWidthFromGeometry ?? profile.width;
        profile.width = newInnerWidth;

        if (pageData?.selectedProfile && Number(pageData.selectedProfile.id) === profileId) {
            pageData.selectedProfile.width = newInnerWidth;
        }
        if (conf && Array.isArray(conf.couplings)) {
            if (profile.type === 'coupling' && typeof profile.id !== 'undefined') {
                conf.couplings.forEach((coupling: any) => {
                    if (Number(coupling.profileId) === Number(profile.id)) {
                        const couplingGap = getFrameGapWidth(coupling);
                        coupling.width = couplingGap ?? newInnerWidth;
                        if (selectedWidth !== undefined) {
                            coupling.widthOut = selectedWidth;
                            coupling.outerWidth = selectedWidth;
                            coupling.selectedWidth = selectedWidth;
                        }
                    }
                });
            } else {
                conf.couplings.forEach((coupling: any) => {
                    if (Number(coupling.profileId) === profileId) {
                        const couplingGap = getFrameGapWidth(coupling);
                        coupling.width = couplingGap ?? newInnerWidth;
                        if (selectedWidth !== undefined) {
                            coupling.widthOut = selectedWidth;
                            coupling.outerWidth = selectedWidth;
                            coupling.selectedWidth = selectedWidth;
                        }
                    }
                });
            }
        }

        if (conf && Array.isArray(conf.UsedProfiles) && (pageData.mullionId || profileId < 0)) {
            const usedProfile = conf.UsedProfiles.find((p: any) => Number(p.id) === profileId);
            const isCouplingBaseProfile = profileId > 0;
            if (usedProfile && !isCouplingBaseProfile) {
                usedProfile.width = newInnerWidth;
                if (selectedWidth !== undefined) {
                    usedProfile.widthOut = selectedWidth;
                    usedProfile.selectedWidth = selectedWidth;
                }
                }
        }
        if (pageData?.profiles && Array.isArray(pageData.profiles) && !pageData.mullionId) {
            const pageProfile = pageData.profiles.find((p: any) => Number(p.id) === profileId);
            const isCouplingBaseProfile = profile.type === 'coupling' && profileId > 0;
            if (pageProfile && !isCouplingBaseProfile) {
                pageProfile.width = newInnerWidth;
                pageData.selectedProfile.widthOut = selectedWidth;
                pageData.selectedProfile.selectedWidth = selectedWidth;
            }
        }

        

        if (conf && Array.isArray(conf.Frames) && pageData.frame && Number.isFinite(pageData.framePosition)) {
            const targetFrame = conf.Frames.find((f: any) => Number(f.id) === Number(pageData.frame.id));
            if (targetFrame && targetFrame.frame[pageData.framePosition]) {
                const frameProfile = targetFrame.frame[pageData.framePosition];
                if (Number(frameProfile.profileId) === profileId) {
                    if (selectedWidth !== undefined) {
                        frameProfile.widthOut = selectedWidth;
                        frameProfile.selectedWidth = selectedWidth;
                    }
                }
            }
        }

        if (conf && Array.isArray(conf.Mullions)) {
            conf.Mullions.forEach((mullion: any) => {
                const matchesMullion = !pageData.mullionId
                    || Number(mullion.id) === Number(pageData.mullionId);
                if (Number(mullion.profileId) === profileId && matchesMullion) {
                    mullion.width = newInnerWidth;
                    if (selectedWidth !== undefined) {
                        mullion.widthOut = selectedWidth;
                        mullion.outerWidth = selectedWidth;
                        mullion.selectedWidth = selectedWidth;
                    }
                }
            });
        }
        if (conf && Array.isArray(conf.Sashes)) {
            const sashes = conf.Sashes;
            sashes.forEach((sash: any) => {
                if (Array.isArray(sash.intMullions)) {
                    sash.intMullions.forEach((intMullion: any) => {
                        const mullionProfile = conf.UsedProfiles?.find((p: any) => Number(p.id) === Number(intMullion.profileId));
                        const effectiveProfileId = mullionProfile?._clonedFrom ?? intMullion.profileId;
                        const matchesProfile = Number(effectiveProfileId) === profileId
                            || Number(intMullion.profileId) === profileId;
                        const matchesMullion = !pageData.mullionId
                            || Number(intMullion.id) === Number(pageData.mullionId);
                        if (matchesProfile && matchesMullion) {
                            if (selectedWidth !== undefined) {
                                intMullion.selectedWidth = selectedWidth;
                                intMullion.widthOut = selectedWidth;
                            }
                            intMullion.width = newInnerWidth;
                            intMullion.widthDeltaInner = deltaValue;
                            const isVertical = intMullion.direction === 'vertical';
                            if (isVertical) {
                                intMullion.rWidth = selectedWidth ?? intMullion.rWidth;
                            } else {
                                intMullion.rHeight = selectedWidth ?? intMullion.rHeight;
                            }
        
                            if (mullionProfile && (!pageData.mullionId || Number(mullionProfile.id) < 0)) {
                                mullionProfile.width = newInnerWidth;
                                if (selectedWidth !== undefined) {
                                    mullionProfile.widthOut = selectedWidth;
                                    mullionProfile.selectedWidth = selectedWidth;
                                }
                            }
                        }
                    });
                }
            });
        }
    }
}
